import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/brand/SeoHead";
import GlowingGridBackground from "@/components/brand/GlowingGridBackground";
import GlassCard from "@/components/brand/GlassCard";
import GradientBorderCard from "@/components/brand/GradientBorderCard";
import FadeUp from "@/components/brand/FadeUp";
import { FloatingAccents } from "@/components/brand/AccentShapes";
import FounderNote from "@/components/sections/FounderNote";
import EarlyStageNote from "@/components/sections/EarlyStageNote";
import aboutHero from "@/assets/brand/about_hero.png";

/* Small SVG icons for the 6 value cards */
const ValueIcon = ({ d, color }) => (
    <svg
        width="28"
        height="28"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
    >
        <path
            d={d}
            stroke={color}
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const VALUES = [
    {
        title: "Confidence through building",
        subtitle:
            "Students learn by finishing real projects, not just by listening.",
        color: "#EC4899",
        icon: "M4 20V8m4 12v-8m4 8v-4m4 4v-10m4 10V6",
    },
    {
        title: "Safe use of modern tools",
        subtitle:
            "AI and digital tools are introduced with clear, age-appropriate guidance.",
        color: "#8B5CF6",
        icon: "M12 3l8 4v6c0 5-3.5 8-8 8s-8-3-8-8V7l8-4z M9 12l2 2 4-4",
    },
    {
        title: "Creativity with practical outcomes",
        subtitle:
            "Imagination is paired with finished work students can actually share.",
        color: "#3B82F6",
        icon: "M12 3v3 M5.6 5.6l2 2 M3 12h3 M5.6 18.4l2-2 M12 18v3 M16.4 16.4l2 2 M18 12h3 M16.4 7.6l2-2 M9 12a3 3 0 1 0 6 0a3 3 0 1 0 -6 0",
    },
    {
        title: "Dignity-first student support",
        subtitle:
            "Every student is met with respect, patience, and a sense of possibility.",
        color: "#14B8A6",
        icon: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M4 21c0-4 4-7 8-7s8 3 8 7",
    },
    {
        title: "Small cohorts, focused guidance",
        subtitle:
            "We work with 5–8 students at a time so support stays close and personal.",
        color: "#FBBF24",
        icon: "M9 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M19 11a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M3 21v-1a5 5 0 0 1 5-5h2a5 5 0 0 1 5 5v1 M14 21v-1a4 4 0 0 1 4-4h2a3 3 0 0 1 3 3v2",
    },
    {
        title: "Community-powered opportunity",
        subtitle:
            "Local sponsors and partners make access free and welcoming for students.",
        color: "#EC4899",
        icon: "M12 21s-7-4.5-7-10a5 5 0 0 1 9-3 5 5 0 0 1 9 3c0 5.5-7 10-7 10",
    },
];

export default function About() {
    return (
        <>
            <SeoHead
                title="About Vibe for Success | Creative Digital Skills for Southwest Florida Students"
                description="Learn about Vibe for Success, Inc. — a Florida nonprofit corporation building creative AI-era workforce readiness for Lee County high school students."
                path="/about"
                image="/about_hero.png"
            />

            {/* ===== Hero ===== */}
            <section
                className="vfs-section relative overflow-hidden"
                data-testid="about-hero"
                style={{ paddingTop: "clamp(2rem, 6vw, 4rem)" }}
            >
                <GlowingGridBackground accent="#8B5CF6" opacity={0.18} />
                <div className="vfs-container relative">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <FadeUp>
                            <span className="vfs-eyebrow">About</span>
                            <h1 className="vfs-h1 mt-5" data-testid="about-h1">
                                Creative digital skills.{" "}
                                <span className="vfs-grad-text">
                                    Real student confidence.
                                </span>{" "}
                                Built for the AI era.
                            </h1>
                            <p
                                className="mt-6 text-lg leading-relaxed max-w-xl"
                                style={{ color: "var(--text-secondary)" }}
                            >
                                Vibe for Success, Inc. is a Florida nonprofit
                                corporation created to help Southwest Florida
                                high school students build practical confidence
                                with modern digital tools.
                            </p>
                        </FadeUp>
                        <FadeUp delay={0.1}>
                            <div className="relative">
                                <FloatingAccents />
                                <img
                                    src={aboutHero}
                                    alt="Creative workspace constellation showing student projects and digital tools."
                                    loading="eager"
                                    fetchPriority="high"
                                    width="900"
                                    height="700"
                                    className="relative w-full h-auto rounded-3xl"
                                    style={{
                                        boxShadow:
                                            "0 30px 80px rgba(139, 92, 246, 0.25)",
                                    }}
                                />
                            </div>
                        </FadeUp>
                    </div>
                </div>
            </section>

            <hr className="vfs-divider max-w-5xl mx-auto" />

            {/* ===== What VFS Is ===== */}
            <section
                className="vfs-section"
                data-testid="about-what-vfs-is"
            >
                <div className="vfs-container">
                    <div className="max-w-3xl">
                        <FadeUp>
                            <h2 className="vfs-h2">
                                What Vibe for Success is
                            </h2>
                            <p
                                className="mt-5 text-lg leading-relaxed"
                                style={{ color: "var(--text-secondary)" }}
                            >
                                Vibe for Success, Inc. is a Florida nonprofit
                                corporation focused on helping Lee County high
                                school students prepare for the AI-era
                                workforce through creative, hands-on digital
                                skills training. Our first program, Future
                                Builders Lab, is a 6-week pilot where students
                                build safe, parent-approved personal portfolio
                                websites using AI-assisted web tools, light
                                HTML/CSS, and digital safety practices.
                            </p>
                        </FadeUp>
                    </div>
                </div>
            </section>

            <hr className="vfs-divider max-w-5xl mx-auto" />

            {/* ===== Why It Exists ===== */}
            <section className="vfs-section" data-testid="about-why">
                <div className="vfs-container">
                    <div className="max-w-3xl">
                        <FadeUp>
                            <h2 className="vfs-h2">Why it exists</h2>
                            <p
                                className="mt-5 text-lg leading-relaxed"
                                style={{ color: "var(--text-secondary)" }}
                            >
                                The way people work is changing quickly.
                                Students need safe, guided experiences with
                                modern tools, and creative students in
                                particular deserve practical pathways into
                                digital careers — not only traditional coding
                                tracks. Vibe for Success was created to make
                                that first step accessible, small-cohort, and
                                finished with something real to show for it.
                            </p>
                        </FadeUp>
                    </div>
                </div>
            </section>

            <hr className="vfs-divider max-w-5xl mx-auto" />

            {/* ===== Who It Serves ===== */}
            <section className="vfs-section" data-testid="about-who">
                <div className="vfs-container">
                    <div className="max-w-3xl">
                        <FadeUp>
                            <h2 className="vfs-h2">Who we serve</h2>
                            <p
                                className="mt-5 text-lg leading-relaxed"
                                style={{ color: "var(--text-secondary)" }}
                            >
                                We serve high school students in Lee County,
                                Florida, with North Fort Myers and Cape Coral
                                as the initial focus area. The first pilot is
                                designed for 5–8 students per cohort. Student
                                participation is free through sponsored seats.
                            </p>
                        </FadeUp>
                    </div>
                </div>
            </section>

            <hr className="vfs-divider max-w-5xl mx-auto" />

            {/* ===== Why Creative Digital Skills Matter ===== */}
            <section className="vfs-section" data-testid="about-creative">
                <div className="vfs-container">
                    <div className="max-w-3xl">
                        <FadeUp>
                            <h2 className="vfs-h2">
                                Why creative digital skills matter
                            </h2>
                            <p
                                className="mt-5 text-lg leading-relaxed"
                                style={{ color: "var(--text-secondary)" }}
                            >
                                Most students will not become professional
                                software engineers — and they don&apos;t need to.
                                What they do need is confidence, judgment,
                                creativity, and practical experience using
                                modern tools responsibly. A finished portfolio
                                website is a concrete, shareable proof of that
                                experience.
                            </p>
                        </FadeUp>
                    </div>
                </div>
            </section>

            <hr className="vfs-divider max-w-5xl mx-auto" />

            {/* ===== Values ===== */}
            <section
                className="vfs-section relative overflow-hidden"
                data-testid="about-values"
            >
                <GlowingGridBackground accent="#14B8A6" opacity={0.1} />
                <div className="vfs-container relative">
                    <FadeUp>
                        <h2 className="vfs-h2">Our values</h2>
                    </FadeUp>
                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {VALUES.map((v, i) => (
                            <FadeUp key={v.title} delay={i * 0.05}>
                                <GlassCard
                                    accentColor={v.color}
                                    className="p-7 h-full"
                                >
                                    <div
                                        className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-xl"
                                        style={{
                                            background: `${v.color}18`,
                                            border: `1px solid ${v.color}45`,
                                        }}
                                    >
                                        <ValueIcon
                                            d={v.icon}
                                            color={v.color}
                                        />
                                    </div>
                                    <h3 className="vfs-h3 mb-2">{v.title}</h3>
                                    <p
                                        className="text-sm leading-relaxed"
                                        style={{
                                            color: "var(--text-secondary)",
                                        }}
                                    >
                                        {v.subtitle}
                                    </p>
                                </GlassCard>
                            </FadeUp>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== Founder note ===== */}
            <FounderNote heading="A note from the founder" />

            {/* ===== Early stage ===== */}
            <EarlyStageNote />

            {/* ===== Bottom CTA strip ===== */}
            <section
                className="vfs-section"
                data-testid="about-bottom-cta"
                style={{ paddingTop: "0" }}
            >
                <div className="vfs-container">
                    <FadeUp>
                        <GradientBorderCard radius={24}>
                            <div className="p-8 sm:p-12 text-center">
                                <h2 className="vfs-h2 mb-7">
                                    Help shape the first pilot.
                                </h2>
                                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                    <Link
                                        to="/sponsor"
                                        className="vfs-cta"
                                        data-testid="about-cta-sponsor"
                                    >
                                        Sponsor a Student
                                    </Link>
                                    <Link
                                        to="/student-interest"
                                        className="vfs-cta-outline"
                                        data-testid="about-cta-interest"
                                    >
                                        Student / Parent Interest
                                    </Link>
                                </div>
                            </div>
                        </GradientBorderCard>
                    </FadeUp>
                </div>
            </section>
        </>
    );
}
